import { useRef } from 'react';
import { Panel } from '@xyflow/react';
import { Hand, HelpCircle, MessageCircle, MousePointer2, Pencil, StickyNote, Type, X, type LucideIcon } from 'lucide-react';
import { useExitTransition } from '../../../hooks/useExitTransition';

/** Una herramienta del modo que reemplaza a Texto · Comentar · Dibujar mientras está (ADR-2026-09-21-el-marcador-de-la-mezcla). */
export interface ReemplazoDeBarra {
  rotulo: string;
  Icono: LucideIcon;
  activa: boolean;
  onCambiar: (activa: boolean) => void;
}

const MS_REEMPLAZO = 180;
/** Texto · Comentar · Dibujar: las que el reemplazo tapa. */
const REEMPLAZABLES: Herramienta[] = ['texto', 'comentar', 'dibujar'];

export type Herramienta = 'seleccionar' | 'mano' | 'texto' | 'comentar' | 'dibujar' | 'nota';

const ITEMS: Array<{ id: Herramienta; label: string; tecla: string; Icono: LucideIcon }> = [
  { id: 'seleccionar', label: 'Seleccionar', tecla: 'V', Icono: MousePointer2 },
  { id: 'mano', label: 'Mover el canvas', tecla: 'H', Icono: Hand },
  { id: 'texto', label: 'Texto', tecla: 'T', Icono: Type },
  { id: 'comentar', label: 'Comentar', tecla: 'C', Icono: MessageCircle },
  { id: 'dibujar', label: 'Dibujar', tecla: 'P', Icono: Pencil },
  { id: 'nota', label: 'Nota', tecla: 'N', Icono: StickyNote },
];

/** Barra de herramientas del canvas, abajo al centro (estilo Higgsfield, tokens Atlas).
 *  La usan Flujos y el lienzo del Agente (ADR-2026-09-16-el-lienzo-toma-la-barra-de-flujos):
 *  la tabla de atajos va por prop porque cada canvas tiene los suyos. */
export const ATAJOS_DE_FLUJOS: Array<[string, string]> = [
  ['V · H · T · C · P · N', 'Seleccionar, mano, texto, comentar, dibujar, nota'],
  ['Click en un puerto', 'Ver qué nodo puede seguir desde ahí'],
  ['Doble click en el fondo', 'Agregar un nodo en ese lugar'],
  ['⌘/Ctrl + Enter', 'Correr todo'],
  ['⌘/Ctrl + D', 'Duplicar la selección'],
  ['⌘/Ctrl + Z · ⇧', 'Deshacer · rehacer'],
  ['Supr', 'Borrar la selección'],
  ['Scroll · ⌘/Ctrl + scroll', 'Mover el canvas · zoom'],
  ['Esc', 'Salir de la herramienta y deseleccionar'],
  ['?', 'Esta ayuda'],
];

export function BarraHerramientas({
  activa,
  onCambiar,
  ayudaAbierta,
  onAyuda,
  atajos = ATAJOS_DE_FLUJOS,
  reemplazo = null,
  orientacion = 'horizontal',
}: {
  activa: Herramienta;
  onCambiar: (h: Herramienta) => void;
  ayudaAbierta: boolean;
  onAyuda: (v: boolean) => void;
  /** La tabla del popover de ayuda; por defecto la de Flujos. */
  atajos?: Array<[string, string]>;
  /** Con él, Texto · Comentar · Dibujar se van (con una transición corta) y queda su botón. */
  reemplazo?: ReemplazoDeBarra | null;
  /**
   * `'horizontal'` (default): abajo al centro, como siempre. `'vertical'`: una columna
   * pegada al borde derecho, a media altura — es como la pide el modo Redes, que tiene la
   * caja de prompt ocupando el pie del lienzo
   * (ADR-2026-10-01-modo-redes). Los mismos botones y atajos; cambian la posición y el eje
   * de los separadores, y la ayuda se abre hacia la izquierda.
   */
  orientacion?: 'horizontal' | 'vertical';
}) {
  // El reemplazo entra y sale con un fundido corto; mientras sale, lo que tapa espera.
  const { mounted: reemplazoMontado, animated: reemplazoVisible } = useExitTransition(!!reemplazo, MS_REEMPLAZO);
  const ultimoReemplazoRef = useRef<ReemplazoDeBarra | null>(null);
  if (reemplazo) ultimoReemplazoRef.current = reemplazo;
  const r = reemplazo ?? ultimoReemplazoRef.current;
  const transicion = 'transition-[opacity,transform] duration-[180ms] ease-out';
  const vertical = orientacion === 'vertical';
  // El separador entre grupos: una raya vertical en la barra horizontal, horizontal en la
  // columna.
  const separador = vertical ? 'my-1 h-px w-5 bg-black/[0.08]' : 'mx-1 h-5 w-px bg-black/[0.08]';
  return (
    <Panel position={vertical ? 'center-right' : 'bottom-center'} className={vertical ? '!m-0 !mr-4' : '!m-0 !mb-4'}>
      <div className={`relative flex items-center gap-0.5 rounded-full border border-black/[0.06] bg-white p-1 shadow-[0_8px_24px_rgba(2,6,43,0.10)] ${vertical ? 'flex-col' : ''}`}>
        {ayudaAbierta && (
          <div className={`absolute w-[380px] rounded-2xl border border-black/[0.06] bg-white p-3 shadow-[0_12px_32px_rgba(2,6,43,0.14)] ${vertical ? 'bottom-0 right-12' : 'bottom-12 left-1/2 -translate-x-1/2'}`}>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[12.5px] font-semibold text-brand-black/90">Atajos del canvas</span>
              <button type="button" onClick={() => onAyuda(false)} className="rounded-full p-0.5 text-neutral-400 hover:bg-surface-elevated hover:text-brand-black">
                <X className="h-4 w-4" />
              </button>
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[12px]">
              {atajos.map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="whitespace-nowrap rounded-md bg-surface-elevated px-1.5 py-0.5 font-mono text-[11px] text-brand-black/80">{k}</dt>
                  <dd className="text-neutral-600">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
        {ITEMS.map(({ id, label, tecla, Icono }, i) => (
          reemplazoMontado && REEMPLAZABLES.includes(id) ? (
            // El primero de los tres deja su lugar al botón del modo; los otros dos, nada.
            id === REEMPLAZABLES[0] && r ? (
              <div key="reemplazo" className={`flex items-center ${vertical ? 'flex-col' : ''}`}>
                <span className={separador} />
                <button
                  type="button"
                  onClick={() => r.onCambiar(!r.activa)}
                  title={r.activa ? `Salir de ${r.rotulo.toLowerCase()} (Esc)` : r.rotulo}
                  aria-pressed={r.activa}
                  className={`flex h-9 items-center gap-1.5 rounded-full text-[13px] font-semibold ${vertical ? 'w-9 justify-center' : 'px-3'} ${transicion} ${
                    reemplazoVisible ? 'scale-100 opacity-100' : 'scale-90 opacity-0'
                  } ${r.activa ? 'bg-[#E8EEFF] text-brand-primary' : 'text-neutral-600 hover:bg-surface-elevated hover:text-brand-black'}`}
                >
                  <r.Icono className="h-[18px] w-[18px]" />
                  {/* En la columna no entra el rótulo: queda el ícono con su `title`. */}
                  {!vertical && r.rotulo}
                </button>
              </div>
            ) : null
          ) : (
          <div key={id} className={`flex items-center ${vertical ? 'flex-col' : ''} ${REEMPLAZABLES.includes(id) ? `${transicion} animate-fade-in` : ''}`}>
            {(i === 2 || i === 5) && <span className={separador} />}
            <button
              type="button"
              onClick={() => onCambiar(activa === id && id !== 'seleccionar' ? 'seleccionar' : id)}
              title={activa === id && id !== 'seleccionar' ? `Salir de ${label.toLowerCase()} (Esc)` : `${label} (${tecla})`}
              aria-pressed={activa === id}
              className={`flex h-9 w-9 items-center justify-center rounded-full transition ${
                activa === id ? 'bg-[#E8EEFF] text-brand-primary' : 'text-neutral-600 hover:bg-surface-elevated hover:text-brand-black'
              }`}
            >
              <Icono className="h-[18px] w-[18px]" />
            </button>
          </div>
          )
        ))}
        <span className={separador} />
        <button type="button" onClick={() => onAyuda(!ayudaAbierta)} title="Atajos (?)" aria-pressed={ayudaAbierta} className={`flex h-9 w-9 items-center justify-center rounded-full transition ${ayudaAbierta ? 'bg-[#E8EEFF] text-brand-primary' : 'text-neutral-600 hover:bg-surface-elevated hover:text-brand-black'}`}>
          <HelpCircle className="h-[18px] w-[18px]" />
        </button>
      </div>
    </Panel>
  );
}
